import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { loadVehicleTypes } from '@/lib/vehicleTypes';
import { Plus, Save, X, Edit, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';

interface ShiftType {
  code: string;
  label: string;
  description: string | null;
  default_colour_hex: string | null;
  allowed_vehicle_types: string[];
  requires_sts: boolean;
  requires_tmv: boolean;
  requires_vehicle: boolean;
  vehicle_mandatory: boolean;
  can_be_open_shift: boolean;
  counts_as_worked_hours: boolean;
  is_dispatcher_role: boolean;
  is_active: boolean;
  sort_order: number;
}

const DEFAULT_COLORS = [
  '#3B82F6', '#6366F1', '#10B981', '#8B5CF6', '#EF4444',
  '#F97316', '#EC4899', '#14B8A6', '#F59E0B', '#64748B',
  '#2563EB', '#0EA5E9', '#7C3AED', '#D946EF', '#84CC16',
];

const RosterPositionsManagement: React.FC = () => {
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ShiftType>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newForm, setNewForm] = useState<Partial<ShiftType>>({
    code: '',
    label: '',
    description: '',
    default_colour_hex: '#3B82F6',
    allowed_vehicle_types: [],
    requires_sts: false,
    requires_tmv: false,
    requires_vehicle: true,
    vehicle_mandatory: false,
    can_be_open_shift: true,
    counts_as_worked_hours: true,
    is_dispatcher_role: false,
    is_active: true,
    sort_order: 100,
  });
  const [saving, setSaving] = useState(false);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<string[]>([]);

  useEffect(() => {
    loadShiftTypes();
    loadVehicleTypes().then((types) => setVehicleTypeOptions(types.map((t) => t.vehicle_type)));
  }, []);

  const loadShiftTypes = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('roster_shift_types')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error loading shift types:', error);
      toast({ title: 'Error', description: 'Failed to load shift types', variant: 'destructive' });
    } else {
      setShiftTypes(data || []);
    }
    setLoading(false);
  };

  const startEdit = (st: ShiftType) => {
    setEditingCode(st.code);
    setEditForm({ ...st });
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editingCode || !editForm.label?.trim()) {
      toast({ title: 'Error', description: 'Label is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('roster_shift_types')
      .update({
        label: editForm.label?.trim(),
        description: editForm.description?.trim() || null,
        default_colour_hex: editForm.default_colour_hex || '#3B82F6',
        allowed_vehicle_types: editForm.allowed_vehicle_types || [],
        requires_sts: editForm.requires_sts ?? false,
        requires_tmv: editForm.requires_tmv ?? false,
        requires_vehicle: editForm.requires_vehicle ?? true,
        vehicle_mandatory: (editForm.requires_vehicle ?? true) && (editForm.vehicle_mandatory ?? false),
        can_be_open_shift: editForm.can_be_open_shift ?? true,
        counts_as_worked_hours: editForm.counts_as_worked_hours ?? true,
        is_dispatcher_role: editForm.is_dispatcher_role ?? false,
        is_active: editForm.is_active ?? true,
        sort_order: editForm.sort_order ?? 100,
        updated_at: new Date().toISOString(),
      })
      .eq('code', editingCode);

    if (error) {
      toast({ title: 'Error', description: 'Failed to update: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: `Position "${editForm.label}" updated` });
      setEditingCode(null);
      setEditForm({});
      loadShiftTypes();
    }
    setSaving(false);
  };

  const addShiftType = async () => {
    if (!newForm.code?.trim() || !newForm.label?.trim()) {
      toast({ title: 'Error', description: 'Code and Label are required', variant: 'destructive' });
      return;
    }
    if (shiftTypes.some(st => st.code === newForm.code?.trim().toUpperCase())) {
      toast({ title: 'Error', description: 'A position with this code already exists', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('roster_shift_types')
      .insert({
        code: newForm.code.trim().toUpperCase(),
        label: newForm.label.trim(),
        description: newForm.description?.trim() || null,
        default_colour_hex: newForm.default_colour_hex || '#3B82F6',
        allowed_vehicle_types: newForm.allowed_vehicle_types || [],
        requires_sts: newForm.requires_sts ?? false,
        requires_tmv: newForm.requires_tmv ?? false,
        requires_vehicle: newForm.requires_vehicle ?? true,
        vehicle_mandatory: (newForm.requires_vehicle ?? true) && (newForm.vehicle_mandatory ?? false),
        can_be_open_shift: newForm.can_be_open_shift ?? true,
        counts_as_worked_hours: newForm.counts_as_worked_hours ?? true,
        is_dispatcher_role: newForm.is_dispatcher_role ?? false,
        is_active: newForm.is_active ?? true,
        sort_order: newForm.sort_order ?? 100,
      });

    if (error) {
      toast({ title: 'Error', description: 'Failed to add: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: `Position "${newForm.label}" added` });
      setShowAddForm(false);
      setNewForm({
        code: '', label: '', description: '', default_colour_hex: '#3B82F6',
        allowed_vehicle_types: [], requires_sts: false, requires_tmv: false, requires_vehicle: true, vehicle_mandatory: false,
        can_be_open_shift: true, counts_as_worked_hours: true, is_dispatcher_role: false, is_active: true, sort_order: 100,
      });
      loadShiftTypes();
    }
    setSaving(false);
  };

  const deleteShiftType = async (code: string) => {
    if (!confirm(`Delete position "${code}"? This cannot be undone.`)) return;
    const { error } = await supabase.from('roster_shift_types').delete().eq('code', code);
    if (error) {
      toast({ title: 'Error', description: 'Failed to delete: ' + error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Deleted', description: `Position "${code}" removed` });
      loadShiftTypes();
    }
  };

  const toggleVehicleType = (form: Partial<ShiftType>, setForm: (f: Partial<ShiftType>) => void, vt: string) => {
    const current = form.allowed_vehicle_types || [];
    if (current.includes(vt)) {
      setForm({ ...form, allowed_vehicle_types: current.filter(v => v !== vt) });
    } else {
      setForm({ ...form, allowed_vehicle_types: [...current, vt] });
    }
  };

  const renderForm = (form: Partial<ShiftType>, setForm: (f: Partial<ShiftType>) => void, isNew: boolean) => (
    <div className="space-y-4 p-4 rounded-lg border border-gray-600 bg-gray-800/50">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {isNew && (
          <div>
            <Label className="text-yellow-500 text-xs">Code (unique ID)</Label>
            <Input
              value={form.code || ''}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="e.g. STS_EARLY"
              className="font-mono"
            />
          </div>
        )}
        <div>
          <Label className="text-yellow-500 text-xs">Label</Label>
          <Input
            value={form.label || ''}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="e.g. STS - Early Shift"
          />
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Description</Label>
          <Input
            value={form.description || ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Optional description"
          />
        </div>
        <div>
          <Label className="text-yellow-500 text-xs">Sort Order</Label>
          <Input
            type="number"
            value={form.sort_order ?? 100}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 100 })}
          />
        </div>
      </div>

      {/* Color picker */}
      <div>
        <Label className="text-yellow-500 text-xs">Colour</Label>
        <div className="flex items-center gap-3 mt-1">
          <input
            type="color"
            value={form.default_colour_hex || '#3B82F6'}
            onChange={(e) => setForm({ ...form, default_colour_hex: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-600 bg-transparent"
          />
          <Input
            value={form.default_colour_hex || '#3B82F6'}
            onChange={(e) => setForm({ ...form, default_colour_hex: e.target.value })}
            className="w-28 font-mono text-sm"
            placeholder="#3B82F6"
          />
          <div className="flex gap-1 flex-wrap">
            {DEFAULT_COLORS.map(c => (
              <button
                key={c}
                onClick={() => setForm({ ...form, default_colour_hex: c })}
                className={`w-6 h-6 rounded-full border-2 transition-all ${form.default_colour_hex === c ? 'border-white scale-110' : 'border-transparent hover:border-gray-400'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Vehicle types */}
      {(form.requires_vehicle ?? true) && (
        <div>
          <Label className="text-yellow-500 text-xs">Allowed Vehicle Types</Label>
          <div className="flex gap-3 mt-1">
            {vehicleTypeOptions.map(vt => (
              <div key={vt} className="flex items-center gap-2">
                <Checkbox
                  id={`vt-${isNew ? 'new' : 'edit'}-${vt}`}
                  checked={(form.allowed_vehicle_types || []).includes(vt)}
                  onCheckedChange={() => toggleVehicleType(form, setForm, vt)}
                />
                <Label htmlFor={`vt-${isNew ? 'new' : 'edit'}-${vt}`} className="text-sm">{vt}</Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flags */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`flag-vehicle-${isNew ? 'new' : 'edit'}`}
            checked={form.requires_vehicle ?? true}
            onCheckedChange={(c) =>
              setForm({
                ...form,
                requires_vehicle: c as boolean,
                allowed_vehicle_types: c ? form.allowed_vehicle_types : [],
              })
            }
          />
          <Label htmlFor={`flag-vehicle-${isNew ? 'new' : 'edit'}`} className="text-xs">Requires Vehicle</Label>
        </div>
        {(form.requires_vehicle ?? true) && (
          <div className="flex items-center gap-2">
            <Checkbox
              id={`flag-vehicle-mandatory-${isNew ? 'new' : 'edit'}`}
              checked={form.vehicle_mandatory ?? false}
              onCheckedChange={(c) => setForm({ ...form, vehicle_mandatory: c as boolean })}
            />
            <Label
              htmlFor={`flag-vehicle-mandatory-${isNew ? 'new' : 'edit'}`}
              className="text-xs"
              title="A shift of this type can't be saved on the roster until a vehicle is chosen"
            >
              Vehicle Mandatory
            </Label>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Checkbox
            id={`flag-sts-${isNew ? 'new' : 'edit'}`}
            checked={form.requires_sts ?? false}
            onCheckedChange={(c) => setForm({ ...form, requires_sts: c as boolean })}
          />
          <Label htmlFor={`flag-sts-${isNew ? 'new' : 'edit'}`} className="text-xs">Requires STS</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`flag-tmv-${isNew ? 'new' : 'edit'}`}
            checked={form.requires_tmv ?? false}
            onCheckedChange={(c) => setForm({ ...form, requires_tmv: c as boolean })}
          />
          <Label htmlFor={`flag-tmv-${isNew ? 'new' : 'edit'}`} className="text-xs">Requires TMV</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`flag-open-${isNew ? 'new' : 'edit'}`}
            checked={form.can_be_open_shift ?? true}
            onCheckedChange={(c) => setForm({ ...form, can_be_open_shift: c as boolean })}
          />
          <Label htmlFor={`flag-open-${isNew ? 'new' : 'edit'}`} className="text-xs">Open Shift</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`flag-hours-${isNew ? 'new' : 'edit'}`}
            checked={form.counts_as_worked_hours ?? true}
            onCheckedChange={(c) => setForm({ ...form, counts_as_worked_hours: c as boolean })}
          />
          <Label htmlFor={`flag-hours-${isNew ? 'new' : 'edit'}`} className="text-xs">Counts Hours</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`flag-active-${isNew ? 'new' : 'edit'}`}
            checked={form.is_active ?? true}
            onCheckedChange={(c) => setForm({ ...form, is_active: c as boolean })}
          />
          <Label htmlFor={`flag-active-${isNew ? 'new' : 'edit'}`} className="text-xs">Active</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`flag-dispatcher-${isNew ? 'new' : 'edit'}`}
            checked={form.is_dispatcher_role ?? false}
            onCheckedChange={(c) => setForm({ ...form, is_dispatcher_role: c as boolean })}
          />
          <Label htmlFor={`flag-dispatcher-${isNew ? 'new' : 'edit'}`} className="text-xs">Dispatcher Role</Label>
        </div>
      </div>

      {/* Preview */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-700">
        <span className="text-xs text-gray-400">Preview:</span>
        <div
          className="px-3 py-1 rounded text-white text-sm font-medium"
          style={{ backgroundColor: form.default_colour_hex || '#3B82F6' }}
        >
          {form.label || 'Position Name'}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button
          onClick={isNew ? addShiftType : saveEdit}
          disabled={saving}
          size="sm"
          className="bg-green-600 hover:bg-green-700"
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? 'Saving...' : isNew ? 'Add Position' : 'Save Changes'}
        </Button>
        <Button
          onClick={isNew ? () => setShowAddForm(false) : cancelEdit}
          variant="outline"
          size="sm"
        >
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-yellow-500">Roster Positions</h2>
          <p className="text-sm text-gray-400 mt-1">
            Manage shift types, colour codes, and position settings used in the roster
          </p>
        </div>
        {!showAddForm && (
          <Button onClick={() => setShowAddForm(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Position
          </Button>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && renderForm(newForm, setNewForm, true)}

      {/* Positions List */}
      <div className="space-y-2">
        {shiftTypes.map((st) => (
          <div key={st.code}>
            {editingCode === st.code ? (
              renderForm(editForm, setEditForm, false)
            ) : (
              <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${st.is_active ? 'border-gray-700 bg-gray-800/30 hover:bg-gray-800/60' : 'border-gray-800 bg-gray-900/30 opacity-60'}`}>
                <GripVertical className="w-4 h-4 text-gray-600 flex-shrink-0" />
                
                {/* Color dot */}
                <div
                  className="w-5 h-5 rounded-full flex-shrink-0 border border-gray-600"
                  style={{ backgroundColor: st.default_colour_hex || '#3B82F6' }}
                />

                {/* Code badge */}
                <Badge
                  variant="outline"
                  className="font-mono text-xs flex-shrink-0"
                  style={{
                    borderColor: st.default_colour_hex || '#3B82F6',
                    color: st.default_colour_hex || '#3B82F6',
                  }}
                >
                  {st.code}
                </Badge>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-200">{st.label}</span>
                  {st.description && (
                    <span className="text-xs text-gray-500 ml-2">{st.description}</span>
                  )}
                </div>

                {/* Flags */}
                <div className="hidden md:flex items-center gap-1 flex-shrink-0">
                  {st.requires_sts && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">STS</Badge>}
                  {st.requires_tmv && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">TMV</Badge>}
                  {st.can_be_open_shift && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-gray-400">Open</Badge>}
                  {st.counts_as_worked_hours && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-gray-400">Hours</Badge>}
                  {st.vehicle_mandatory && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-gray-400">Vehicle needed</Badge>}
                  {st.is_dispatcher_role && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-sky-900/50 text-sky-300">Dispatcher</Badge>}
                  {!st.is_active && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Inactive</Badge>}
                </div>

                {/* Vehicle types */}
                <div className="hidden lg:flex items-center gap-1 flex-shrink-0">
                  {st.allowed_vehicle_types.map(vt => (
                    <span key={vt} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">{vt}</span>
                  ))}
                </div>

                {/* Sort order */}
                <span className="text-xs text-gray-500 w-8 text-right flex-shrink-0">#{st.sort_order}</span>

                {/* Actions */}
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => startEdit(st)}
                    className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteShiftType(st.code)}
                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {shiftTypes.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No positions configured. Click "Add Position" to create one.
          </div>
        )}
      </div>
    </div>
  );
};

export default RosterPositionsManagement;
